/**
 * Spanish Translations — Impact Core i18n
 *
 * Flat key-value structure. Keys use dot notation for namespacing.
 * Coverage: navigation, actions, statuses, dashboard labels, form labels.
 */

export const es: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Panel de control',
  'nav.funds': 'Fondos',
  'nav.investments': 'Inversiones',
  'nav.investees': 'Participadas',
  'nav.esg': 'ESG Revisión',
  'nav.compliance': 'Cumplimiento',
  'nav.reports': 'Informes',
  'nav.settings': 'Configuración',
  'nav.users': 'Usuarios',
  'nav.sfdr': 'SFDR',
  'nav.pai': 'PAI Indicadores',
  'nav.taxonomy': 'Taxonomía de la UE',
  'nav.filings': 'Presentaciones',
  'nav.calendar': 'Calendario',
  'nav.submit': 'Enviar datos',
  'nav.submissions': 'Mis envíos',
  'nav.benchmarks': 'Índices de referencia',
  'nav.messages': 'Mensajes',
  'nav.portfolio': 'Cartera',
  'nav.performance': 'Rendimiento de fondos',
  'nav.esgSummary': 'ESG Resumen',
  'nav.pipeline': 'Pipeline',
  'nav.dueDiligence': 'Due Diligence',
  'nav.impact': 'Métricas de impacto',
  'nav.sdg': 'Mapeo ODS',
  'nav.audit': 'Pista de auditoría',
  'nav.snapshots': 'Instantáneas de datos',
  'nav.knowledgeGraph': 'Grafo de conocimiento',
  'nav.geographic': 'Geográfico',
  'nav.bi': 'BI Estudio',
  'nav.ai': 'Asistente IA',
  'nav.tasks': 'Tareas',
  'nav.csrd': 'CSRD',
  'nav.regulatory': 'Monitor regulatorio',

  // Common Actions
  'action.create': 'Crear',
  'action.edit': 'Editar',
  'action.delete': 'Eliminar',
  'action.save': 'Guardar',
  'action.cancel': 'Cancelar',
  'action.submit': 'Enviar',
  'action.approve': 'Aprobar',
  'action.reject': 'Rechazar',
  'action.download': 'Descargar',
  'action.export': 'Exportar',
  'action.import': 'Importar',
  'action.filter': 'Filtrar',
  'action.search': 'Buscar',
  'action.refresh': 'Actualizar',
  'action.close': 'Cerrar',
  'action.back': 'Atrás',
  'action.next': 'Siguiente',
  'action.previous': 'Anterior',
  'action.viewAll': 'Ver todo',
  'action.viewDetails': 'Ver detalles',
  'action.generate': 'Generar',
  'action.signOut': 'Cerrar sesión',
  'action.addNew': 'Añadir nuevo',
  'action.invite': 'Invitar',

  // Statuses
  'status.draft': 'Borrador',
  'status.submitted': 'Enviado',
  'status.validated': 'Validado',
  'status.approved': 'Aprobado',
  'status.rejected': 'Rechazado',
  'status.pending': 'Pendiente',
  'status.inProgress': 'En curso',
  'status.overdue': 'Vencido',
  'status.compliant': 'Conforme',
  'status.filed': 'Presentado',
  'status.active': 'Activo',
  'status.inactive': 'Inactivo',
  'status.closed': 'Cerrado',
  'status.fundraising': 'Captación de fondos',
  'status.investing': 'Inversión',
  'status.harvesting': 'Cosecha',
  'status.validating': 'Validando',
  'status.review': 'En revisión',
  'status.monitoring': 'Monitoreo',
  'status.exiting': 'Salida',
  'status.pipeline': 'Pipeline',
  'status.due_diligence': 'Debida diligencia',
  'status.disbursed': 'Desembolsado',
  'status.restructured': 'Reestructurado',
  'status.matured': 'Vencido',
  'status.written_off': 'Cancelado',
  'status.exited': 'Salido',
  'status.incomplete': 'Incompleto',
  'status.open': 'Abierto',
  'status.in_progress': 'En progreso',
  'status.completed': 'Completado',
  'status.cancelled': 'Cancelado',
  'status.accepted': 'Aceptado',
  'status.failed': 'Fallido',

  // Dashboard Labels
  'dashboard.portfolioOverview': 'Visión general de la cartera',
  'dashboard.complianceStatus': 'Estado de cumplimiento',
  'dashboard.collectionProgress': 'Progreso de recopilación',
  'dashboard.activityFeed': 'Actividad',
  'dashboard.quickActions': 'Acciones rápidas',
  'dashboard.investmentPipeline': 'Pipeline de inversión',
  'dashboard.totalFunds': 'Total de fondos',
  'dashboard.aum': 'AUM',
  'dashboard.activeInvestments': 'Inversiones activas',
  'dashboard.totalInvestees': 'Participadas',
  'dashboard.recentActivity': 'Actividad reciente',

  // LP Dashboard Labels
  'lp.totalCommitted': 'Total comprometido',
  'lp.capitalCalled': 'Capital llamado',
  'lp.distributed': 'Distribuido',
  'lp.netPosition': 'Posición neta',
  'lp.tvpi': 'TVPI',
  'lp.dpi': 'DPI',
  'lp.fundPerformance': 'Rendimiento de fondos',
  'lp.esgSummary': 'ESG Resumen',
  'lp.reportLibrary': 'Biblioteca de informes',
  'lp.taxonomyAligned': 'Alineado con taxonomía',
  'lp.paiCoverage': 'PAI Cobertura',
  'lp.portfolioPerformance': 'Rendimiento de cartera LP',

  // Fund Labels
  'fund.name': 'Nombre del fondo',
  'fund.code': 'Código del fondo',
  'fund.vintage': 'Año de constitución',
  'fund.size': 'Tamaño del fondo',
  'fund.targetSize': 'Tamaño objetivo',
  'fund.currency': 'Moneda',
  'fund.strategy': 'Estrategia',
  'fund.status': 'Estado',
  'fund.sfdrCategory': 'SFDR Categoría',
  'fund.geographyFocus': 'Enfoque geográfico',
  'fund.sectorFocus': 'Enfoque sectorial',

  // Form Labels
  'form.name': 'Nombre',
  'form.email': 'Correo electrónico',
  'form.country': 'País',
  'form.amount': 'Importe',
  'form.currency': 'Moneda',
  'form.date': 'Fecha',
  'form.description': 'Descripción',
  'form.notes': 'Notas',
  'form.type': 'Tipo',
  'form.period': 'Período',
  'form.year': 'Año',
  'form.required': 'Obligatorio',
  'form.optional': 'Opcional',

  // Table Headers
  'table.fund': 'Fondo',
  'table.commitment': 'Compromiso',
  'table.called': 'Llamado',
  'table.distributed': 'Distribuido',
  'table.tvpi': 'TVPI',
  'table.status': 'Estado',
  'table.regulation': 'Regulación',
  'table.filingType': 'Tipo de presentación',
  'table.reportingPeriod': 'Período',
  'table.createdAt': 'Creado',
  'table.actions': 'Acciones',

  // Compliance
  'compliance.sfdr': 'SFDR',
  'compliance.taxonomy': 'Taxonomía de la UE',
  'compliance.pai': 'PAI',
  'compliance.dataCompleteness': 'Completitud de datos',
  'compliance.nextDeadline': 'Próximo plazo',

  // Reports
  'report.quarterly': 'Informe trimestral',
  'report.annual': 'Informe anual',
  'report.paiStatement': 'PAI Declaración',
  'report.taxonomyReport': 'Informe de taxonomía',
  'report.generatedAt': 'Generado',
  'report.version': 'Versión',

  // General
  'general.loading': 'Cargando...',
  'general.noData': 'No hay datos disponibles',
  'general.error': 'Se produjo un error',
  'general.retry': 'Reintentar',
  'general.total': 'Total',
  'general.page': 'Página',
  'general.of': 'de',
  'general.showing': 'Mostrando',
  'general.results': 'resultados',
  'general.language': 'Idioma',
  'general.english': 'Inglés',
  'general.french': 'Francés',

  // Tour
  'tour.takeTour': 'Hacer el recorrido',
  'tour.skip': 'Omitir',
  'tour.next': 'Siguiente',
  'tour.prev': 'Anterior',
  'tour.finish': 'Finalizar',


  // Tour Content, Form Labels, Table Headers, Actions, Errors (i18n wiring)
  'tour.sidebarTitle': 'Navegación',
  'tour.sidebarContent': 'Use la barra lateral para navegar entre gestión de cartera, recopilación de datos ESG, cumplimiento, informes y herramientas de inteligencia.',
  'tour.kpisTitle': 'Resumen de cartera',
  'tour.kpisContent': 'Estas tarjetas muestran su cartera de un vistazo — fondos totales, AUM, inversiones activas y número de participadas.',
  'tour.actionsTitle': 'Acciones rápidas',
  'tour.actionsContent': 'Cree fondos, añada inversiones, invite participadas y genere informes con un clic.',
  'tour.languageTitle': 'Idioma',
  'tour.languageContent': 'Cambie entre 24 idiomas oficiales de la UE. Toda la interfaz se traduce instantáneamente.',
  'tour.aiTitle': 'Asistente IA',
  'tour.aiContent': 'Haga preguntas sobre su cartera en cualquier momento. El asistente IA tiene contexto completo sobre sus fondos e inversiones.',
  'action.createFund': 'Crear fondo',
  'action.editFund': 'Editar fondo',
  'action.addInvestment': 'Añadir inversión',
  'action.addInvestee': 'Añadir participada',
  'action.editInvestee': 'Editar participada',
  'action.createTemplate': 'Crear plantilla',
  'action.createSubmission': 'Crear envío',
  'action.createFiling': 'Crear presentación',
  'action.generateReport': 'Generar informe',
  'action.inviteUser': 'Invitar usuario',
  'action.sendInvitation': 'Enviar invitación',
  'action.saveChanges': 'Guardar cambios',
  'form.fundName': 'Nombre del fondo',
  'form.fundCode': 'Código del fondo',
  'form.sfdrCategory': 'Categoría SFDR',
  'form.baseCurrency': 'Moneda base',
  'form.targetSize': 'Tamaño objetivo',
  'form.vintageYear': 'Año de constitución',
  'form.geographyFocus': 'Enfoque geográfico',
  'form.sectorFocus': 'Enfoque sectorial',
  'form.investmentCode': 'Código de inversión',
  'form.instrumentType': 'Tipo de instrumento',
  'form.maturityDate': 'Fecha de vencimiento',
  'form.sector': 'Sector',
  'form.countryCode': 'Código de país',
  'form.entityType': 'Tipo de entidad',
  'form.region': 'Región',
  'form.staffCount': 'Número de empleados',
  'form.borrowers': 'Prestatarios',
  'form.contactName': 'Nombre de contacto',
  'form.contactEmail': 'Correo de contacto',
  'form.templateName': 'Nombre de plantilla',
  'form.templateType': 'Tipo de plantilla',
  'form.indicators': 'Indicadores',
  'form.reportingPeriod': 'Período de reporte',
  'form.reportingYear': 'Año de reporte',
  'form.regulation': 'Regulación',
  'form.filingType': 'Tipo de presentación',
  'form.reportType': 'Tipo de informe',
  'form.emailAddress': 'Dirección de correo',
  'form.role': 'Rol',
  'form.selectFund': 'Seleccionar fondo...',
  'form.selectInvestee': 'Seleccionar participada...',
  'table.code': 'Código',
  'table.investee': 'Participada',
  'table.instrument': 'Instrumento',
  'table.amount': 'Importe',
  'table.currency': 'Moneda',
  'table.maturity': 'Vencimiento',
  'table.name': 'Nombre',
  'table.country': 'País',
  'table.type': 'Tipo',
  'table.period': 'Período',
  'table.completion': 'Completitud',
  'table.submitted': 'Enviado',
  'table.version': 'Versión',
  'table.indicators': 'Indicadores',
  'table.dueDate': 'Fecha límite',
  'table.generated': 'Generado',
  'table.email': 'Correo',
  'table.role': 'Rol',
  'error.createFailed': 'Error al crear. Inténtelo de nuevo.',
  'error.updateFailed': 'Error al actualizar. Inténtelo de nuevo.',
  'error.loadFailed': 'Error al cargar los datos.',

  // Sidebar Sections
  'sections.portfolio': 'CARTERA',
  'sections.dataCollection': 'RECOPILACIÓN DE DATOS',
  'sections.intelligence': 'INTELIGENCIA',
  'sections.ecosystem': 'ECOSISTEMA',
  'sections.admin': 'ADMINISTRACIÓN',
  'sections.other': 'OTROS',

  // Page Titles
  'pages.messagesTitle': 'Mensajes',
  'pages.taxonomyTitle': 'Taxonomía UE',
  'pages.biStudioTitle': 'BI Studio',
  'pages.knowledgeGraphTitle': 'Grafo de Conocimiento',
  'pages.benchmarksTitle': 'Benchmarks',
  'pages.mcpTitle': 'Integración MCP',
  'pages.tasksTitle': 'Tareas',
  'pages.submitDataTitle': 'Enviar datos ESG',

  // Empty States
  'empty.benchmarksComingSoon': 'Benchmarks próximamente',

  // Labels
  'labels.sfdr': 'SFDR',
  'labels.euTaxonomy': 'Taxonomía UE',
  'labels.csrd': 'CSRD',
  'labels.mifid': 'MiFID',
  'labels.other': 'Otro',
  'labels.critical': 'Crítico',
  'labels.high': 'Alto',
  'labels.medium': 'Medio',
  'labels.low': 'Bajo',

  // Count / summary strings
  'count.fundsTotal': '{count} fund(s) total',
  'count.investmentsTotal': '{count} investment(s) total',
  'count.investeesTotal': '{count} investee(s) total',

  // Filter options
  'filter.allStatus': 'All Status',
  'filter.allSfdr': 'All SFDR',

  // Empty states
  'empty.noFundsYet': 'No funds yet. Create your first fund.',
  'empty.noFundsMatch': 'No funds match your filters.',
  'empty.noInvestmentsYet': 'No investments yet.',
  'empty.noInvestmentsMatch': 'No investments match your search.',
  'empty.noInvesteesYet': 'No investees yet.',
  'empty.noInvesteesMatch': 'No investees match your search.',
  'empty.noTemplatesYet': 'No ESG templates configured yet.',
  'empty.noSubmissionsYet': 'No ESG submissions yet.',
  'empty.noComplianceData': 'No SFDR compliance data available.',
  'empty.noFilingsYet': 'No compliance filings yet.',
  'empty.noReportsYet': 'No reports generated yet.',
  'empty.selectFundPai': 'Select a fund to view PAI indicators.',
  'empty.noPaiIndicators': 'No PAI indicators found for this fund.',
  'empty.selectFundCalendar': 'Select a fund to view compliance calendar.',
  'empty.noCalendarEvents': 'No upcoming events for this fund.',

  // Page subtitles
  'subtitle.esg': 'Templates and data submissions',
  'subtitle.compliance': 'SFDR, PAI indicators, and regulatory filings',
  'subtitle.reports': 'Generate and manage compliance reports',
  'subtitle.settings': 'Manage your account and preferences',

  // Tab labels
  'tab.templates': 'Templates',
  'tab.submissions': 'Submissions',
  'tab.sfdr': 'SFDR',
  'tab.paiIndicators': 'PAI Indicators',
  'tab.filings': 'Filings',
  'tab.reports': 'Reports',
  'tab.calendar': 'Calendar',

  // Compliance table headers
  'table.indicator': 'Indicator',
  'table.category': 'Category',
  'table.metric': 'Metric',
  'table.value': 'Value',
  'table.mandatory': 'Mandatory',
  'table.data': 'Data',
  'table.deadline': 'Deadline',
  'table.taxonomy': 'Taxonomy',

  // Yes/No
  'general.yes': 'Yes',
  'general.no': 'No',

  // Error messages
  'error.loadTemplates': 'Failed to load templates.',
  'error.loadSubmissions': 'Failed to load submissions.',
  'error.loadCompliance': 'Failed to load compliance data.',
  'error.loadPai': 'Failed to load PAI indicators.',
  'error.loadFilings': 'Failed to load filings.',
  'error.loadCalendar': 'Failed to load calendar events.',
  'error.createTemplate': 'Failed to create template. Please try again.',
  'error.createSubmission': 'Failed to create submission. Please try again.',
  'error.createFiling': 'Failed to create filing. Please try again.',
  'error.generateReport': 'Failed to generate report.',
  'error.templateNameRequired': 'Template name is required',
  'error.selectFund': 'Select a fund',
  'error.selectInvestee': 'Select an investee',
  'error.validAmount': 'Enter a valid amount',
  'error.reportingPeriodRequired': 'Reporting period is required',
  'error.validYearRequired': 'Valid year is required',
  'error.enterPeriod': 'Enter a period',
  'error.nameRequired': 'Name is required',
  'error.countryCodeRequired': 'Country code is required',
  'error.operationFailed': 'Operation failed.',
  'error.pleaseselectFund': 'Please select a fund',

  // Success messages
  'success.submissionCreated': 'Submission created successfully.',
  'success.profileSaved': 'Profile saved successfully.',

  // Relative time
  'time.justNow': 'Just now',
  'time.minutesAgo': '{count}m ago',
  'time.hoursAgo': '{count}h ago',
  'time.daysAgo': '{count}d ago',

  // Settings
  'settings.profile': 'Profile',
  'settings.organization': 'Organization',
  'settings.apiKeys': 'API Keys',
  'settings.notifications': 'Notifications',
  'settings.orgId': 'Org ID',
  'settings.tier': 'Tier',
  'settings.apiKeysDescription': 'Contact your organization administrator to manage API keys and integration credentials.',
  'settings.apiKeysNote': 'API keys provide programmatic access to Impact Core data. For security, key creation and revocation is restricted to organization admins.',
  'settings.notifReportAlerts': 'Report generation alerts',
  'settings.notifReportAlertsDesc': 'Email when a report is generated or fails',
  'settings.notifComplianceReminders': 'Compliance deadline reminders',
  'settings.notifComplianceRemindersDesc': 'Email reminders for upcoming filing deadlines',
  'settings.notifEsgUpdates': 'ESG submission updates',
  'settings.notifEsgUpdatesDesc': 'Email when investee submissions change status',
  'settings.notifInAppActivity': 'In-app activity feed',
  'settings.notifInAppActivityDesc': 'Show real-time activity notifications in header',

  // Data Completeness label
  'compliance.dataCompletenessLabel': 'Data Completeness',
};
